import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { UseStatusDecision, LatestCheckLine } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ClipboardText, WarningOctagon, Gauge, Books, Wrench, Database, SlidersHorizontal, ShieldWarning, FileText, CheckCircle, GasPump, Waveform } from "@phosphor-icons/react";

const ICONS = { start_check: ClipboardText, report_fault: WarningOctagon, update_reading: Gauge,
  view_documents: Books, maintenance: Wrench, quick_data: Database, assess: SlidersHorizontal,
  havs_start: Waveform, havs_stop: Waveform };

const fuelClass = (t) => {
  const k = (t || "").toLowerCase();
  if (k === "petrol") return "bg-emerald-600 text-white border-emerald-800";
  if (k === "diesel") return "bg-slate-900 text-white border-black";
  if (k === "electric") return "bg-blue-600 text-white border-blue-800";
  if (k.includes("hybrid")) return "bg-teal-600 text-white border-teal-800";
  if (k === "lpg" || k === "hvo") return "bg-amber-500 text-slate-900 border-amber-700";
  return "bg-slate-600 text-white border-slate-800";
};

export default function TagResolvePage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [havsMe, setHavsMe] = useState(null);

  useEffect(() => {
    setRes(null);
    api.get(`/t/${token}/resolve`).then((r) => setRes(r.data)).catch((e) => setErr(e.response?.data?.detail || "Could not resolve tag"));
  }, [token]);

  useEffect(() => {
    if (res?.havs) api.get("/havs/me").then((r) => setHavsMe(r.data)).catch(() => {});
  }, [res?.havs, res?.tag_id]);

  const acknowledge = async () => {
    try { await api.post(`/rams/${res.rams.id}/acknowledge`, {}); toast.success("Acknowledged & signed");
      const r = await api.get(`/t/${token}/resolve`); setRes(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (err) return <div className="max-w-md mx-auto pt-10 text-center"><p className="font-display text-2xl font-black text-red-600">{err}</p></div>;
  if (!res) return <p className="text-slate-500">Resolving tag…</p>;

  if (res.target_type === "rams") {
    const rev = res.current_revision;
    return (
      <div className="flex flex-col gap-5 max-w-2xl mx-auto">
        {res.action_required && (
          <div className="bg-red-600 text-white border-y-4 border-red-800 py-5 px-4 text-center">
            <ShieldWarning size={36} weight="fill" className="mx-auto" />
            <div className="font-display text-2xl font-black uppercase tracking-widest mt-1">Action Required</div>
          </div>
        )}
        <div className="px-1">
          <div className="font-mono text-sm font-bold text-slate-500">{res.rams.ref} {rev ? `· Rev ${rev.revision}` : ""}</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">{res.rams.title}</h1>
        </div>
        {rev?.summary && <p className="font-body text-slate-700 whitespace-pre-wrap bg-white border-2 border-slate-200 rounded-lg p-4">{rev.summary}</p>}
        {rev?.file_url && <a href={rev.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 font-semibold"><FileText size={18} weight="bold" />Open document</a>}
        {res.acknowledged
          ? <div className="inline-flex items-center gap-2 text-emerald-700 font-bold uppercase"><CheckCircle size={20} weight="fill" />Acknowledged</div>
          : <Button data-testid="tag-rams-acknowledge" onClick={acknowledge} className="h-14 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase text-lg">Acknowledge & Sign</Button>}
      </div>
    );
  }

  if (res.target_type !== "asset") {
    return <div className="max-w-md mx-auto pt-6"><p className="font-body text-slate-700">Tag points to: {res.target_type}</p></div>;
  }

  const asset = res.asset;
  const doHavs = async (start) => {
    try {
      const body = start ? { asset_id: asset.id } : { asset_id: asset.id, session_id: res.havs?.open_session_id };
      await api.post(`/havs/${start ? "start" : "stop"}`, body);
      toast.success(start ? "Started — exposure is being logged" : "Stopped — exposure logged");
      const r = await api.get(`/t/${token}/resolve`); setRes(r.data);
      const m = await api.get("/havs/me"); setHavsMe(m.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const act = (key) => {
    if (key === "havs_start") return doHavs(true);
    if (key === "havs_stop") return doHavs(false);
    if (key === "start_check") nav(`/assets/${asset.id}/check`);
    else if (key === "report_fault") nav(`/faults/report?asset=${asset.id}`);
    else nav(`/assets/${asset.id}`);
  };

  return (
    <div className="flex flex-col gap-5 max-w-2xl mx-auto -mt-4 sm:-mt-6 md:-mt-8">
      {/* Authoritative USE STATUS decision ALWAYS first, visually dominant */}
      <UseStatusDecision decision={res.use_status} />

      <div className="px-1 text-center">
        <div className="font-mono text-sm font-bold text-slate-500">{asset.asset_number}</div>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">{asset.name}</h1>
        {asset.registration && <div className="font-mono text-lg text-slate-700">{asset.registration}</div>}
      </div>

      {/* Latest check shown SEPARATELY — never overrides the Use Status above */}
      <div className="bg-white border-2 border-slate-200 rounded-lg p-3">
        <LatestCheckLine lc={res.last_check} />
      </div>

      {asset.battery_platform_id && (
        <Button data-testid="tag-log-havs" onClick={() => nav(`/havs?battery=${asset.id}`)}
          className="h-14 bg-blue-600 hover:bg-blue-700 font-bold uppercase text-lg tracking-wide">
          Log HAVS Use
        </Button>
      )}

      {asset.fuel_type && (
        <div data-testid="tag-fuel" className={`flex items-center justify-center gap-3 border-4 rounded-lg py-3 px-4 ${fuelClass(asset.fuel_type)}`}>
          <GasPump size={30} weight="fill" />
          <span className="font-display text-2xl font-black uppercase tracking-widest">Fuel: {asset.fuel_type}</span>
        </div>
      )}



      {res.havs && (
        <div data-testid="tag-havs" className={`border-4 rounded-lg py-3 px-4 ${res.havs.open_session_id ? "border-red-500 bg-red-50" : "border-amber-500 bg-amber-50"}`}>
          <div className="flex items-center gap-2 font-display text-lg font-black uppercase tracking-wide text-slate-800">
            <Waveform size={24} weight="bold" />HAVS tool · {res.havs.magnitude} m/s²
          </div>
          {havsMe && (
            <div className="mt-1 font-body text-sm text-slate-700">
              Your exposure today: <b>{havsMe.total_points} pts</b>
              {havsMe.status === "elv" ? <span className="ml-2 text-red-700 font-bold uppercase">Over daily limit (ELV {havsMe.elv})</span>
                : havsMe.status === "eav" ? <span className="ml-2 text-amber-700 font-bold uppercase">Action value reached (EAV {havsMe.eav})</span>
                : <span className="ml-2 text-emerald-700">of {havsMe.eav} (EAV)</span>}
            </div>
          )}
          {res.havs.open_session_id && <div className="mt-1 text-sm font-semibold text-red-700">In use by you — tap “Stop Using” when finished.</div>}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {res.actions.map((a) => {
          const Icon = ICONS[a.key] || ClipboardText;
          return (
            <Button key={a.key} data-testid={`tag-action-${a.key}`} onClick={() => act(a.key)}
              className={`h-20 flex-col gap-1 font-bold uppercase text-sm ${a.primary ? "bg-blue-600 hover:bg-blue-700 col-span-2" : "bg-slate-900 hover:bg-slate-800"}`}>
              <Icon size={26} weight="bold" /> {a.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
